import { Box, Button, Checkbox, Flex, Image, Tooltip } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { selectedCrossReferences } from '../../atoms/rootAtom';
import { CrossReference, PaperOutline, PaperOutlineNode } from '../../types/Writer/OutlineNode';
import { getCrossReferenceNodes } from '../../utils/flatternPaperNodes';
import LatexWrapper from '../LatexWrapper';
import { pxToRem } from "../../utils/editor.ts";
import { TableRender } from "../PaperEditor/InserTool/TableRender.tsx";
import { OutlineNodeType } from '../../types/Writer/OutlineNode';

enum REFERENCE_TYPE {
  figure = 'figure',
  table = 'table',
  equation = 'equation',
  paragraph = 'paragraph',
  section = 'section'
}

type CrossReferencePanelProps = {
  outline: PaperOutline
  editor: any
}

const CrossReferencePanel: React.FC<CrossReferencePanelProps> = ({ outline, editor }) => {
  const [currentReferenceType, setCurrentReferenceType] = useState<REFERENCE_TYPE>(REFERENCE_TYPE.figure);
  const [selectedReferences, setSelectedReferences] = useRecoilState(selectedCrossReferences);
  const [crossRef, setCrossRef] = useState<CrossReference | null>(undefined);

  useEffect(() => {
    if (outline && outline.nodes) {
      const referData = getCrossReferenceNodes(outline.nodes)
      setCrossRef(referData)
    }
  }, [outline])


  const toggleSelection = (reference: PaperOutlineNode) => {
    setSelectedReferences((prevSelected) => {
      const exists = prevSelected.some((ref) => ref.id === reference.id);
      if (exists) {
        return prevSelected.filter((ref) => ref.id !== reference.id);
      } else {
        return [...prevSelected, reference];
      }
    });
  }

  const crossReferencePillarOnClick = (piller: REFERENCE_TYPE) => {
    setCurrentReferenceType(REFERENCE_TYPE[piller])
  }

  // 做个映射
  const typeEnum = {
    "latex_figure": "figure",
    "latex_table": "table",
    "figure_table": "table",
    "latex_figure_table": "table",
  }

  /**
   * insert table/figure/equation/section refer
   * @param e
   */
  const insertReference = (e) => {
    e.stopPropagation()
    selectedReferences.forEach((ref) => {
      const type: string = typeEnum[ref.type] || ref.type

      if ([OutlineNodeType.Figure, OutlineNodeType.LatexFigure].includes(ref.type)) {
        editor?.commands.insertReference({
          type: type,
          content: ref.label || ref.name,
          tooltip: ref.img_url || ref.content,
        })
      } else{
        editor?.commands.insertReference({
          type: type,
          content: ref.label || ref.name,
          tooltip: ref.content || ref.name || ref.caption || ref.latex || ref.img_url ,
        })
      }
      
    })
    setSelectedReferences([])
  }

  return (
      <Flex flexDir={'column'} onClick={(e) => e.stopPropagation()}>
        <Flex flexDir={'row'} pb={1} px={'0.5rem'}>
          <Button size={'xs'}
                  flex={1}
                  mr={1}
                  color={"#333"}
                  borderRadius={pxToRem(10)}
                  border={'1px solid rgba(56, 152, 255, 0.38)'}
                  _hover={{ bg: 'rgba(56, 152, 255, 0.38)' }}
                  bg={currentReferenceType === 'figure' ? 'rgba(56, 152, 255, 0.38)' : 'transparent'}
                  onClick={() => crossReferencePillarOnClick(REFERENCE_TYPE.figure)}>
            Figure
          </Button>
          <Button size={'xs'}
                  flex={1}
                  mr={1}
                  color={"#333"}
                  borderRadius={pxToRem(10)}
                  border={'1px solid rgba(56, 152, 255, 0.38)'}
                  _hover={{ bg: 'rgba(56, 152, 255, 0.38)' }}
                  bg={currentReferenceType === 'table' ? 'rgba(56, 152, 255, 0.38)' : 'transparent'}
                  onClick={() => crossReferencePillarOnClick(REFERENCE_TYPE.table)}>
            Table
          </Button>
          <Button size={'xs'}
                  flex={1}
                  mr={1}
                  color={"#333"}
                  border={'1px solid rgba(56, 152, 255, 0.38)'}
                  borderRadius={pxToRem(10)}
                  _hover={{ bg: 'rgba(56, 152, 255, 0.38)' }}
                  bg={currentReferenceType === 'equation' ? 'rgba(56, 152, 255, 0.38)' : 'transparent'}
                  onClick={() => crossReferencePillarOnClick(REFERENCE_TYPE.equation)}>
            Equation
          </Button>
          <Button size={'xs'}
                  flex={1}
                  mr={1}
                  color={"#333"}
                  border={'1px solid rgba(56, 152, 255, 0.38)'}
                  borderRadius={pxToRem(10)}
                  _hover={{ bg: 'rgba(56, 152, 255, 0.38)' }}
                  bg={currentReferenceType === 'section' ? 'rgba(56, 152, 255, 0.38)' : 'transparent'}
                  onClick={() => crossReferencePillarOnClick(REFERENCE_TYPE.section)}>
            Section
          </Button>
        </Flex>
        <Flex flexDir={'column'} overflow={'auto'} h={pxToRem(80)}>
          {crossRef && crossRef?.section.map((ref, index) => (
              <Box key={index}>
                {currentReferenceType === REFERENCE_TYPE.section &&
                    <Flex pt={1} onClick={(e) => {
                      e.stopPropagation()
                      toggleSelection(ref)
                    }}>
                        <Checkbox
                            isChecked={selectedReferences.includes(ref)}
                            onChange={() => toggleSelection(ref)}>
                            <Tooltip hasArrow
                                     bg={'#FFF'} color={'#333'}
                                     label={JSON.stringify(ref.name)} aria-label='Description' style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                            }}>
                                <span>{ref.label || ref.name}</span>
                            </Tooltip>
                        </Checkbox>
                    </Flex>
                }
              </Box>
          ))}
          {crossRef && crossRef?.equation.map((ref, index) => (
              <Box key={index}>
                {currentReferenceType === REFERENCE_TYPE.equation &&
                    <Flex pt={1} onClick={(e) => {
                      e.stopPropagation()
                      toggleSelection(ref)
                    }}>
                        <Checkbox
                            isChecked={selectedReferences.includes(ref)}
                            onChange={() => toggleSelection(ref)}>
                            <Tooltip bg={'#FFF'} color={'#333'} hasArrow label={
                              <Box minW={pxToRem(300)} borderRadius={4}>
                                {
                                  ref.latex ? <Box>{ref.latex}</Box> : <LatexWrapper content={ref.content}></LatexWrapper>
                                }
                              </Box>
                            } aria-label='Description'>
                              {ref.label || <LatexWrapper content={ref.content}></LatexWrapper>}
                            </Tooltip>
                        </Checkbox>
                    </Flex>
                }
              </Box>
          ))}
          {crossRef && crossRef?.figure.map((ref, index) => (
              <Box key={index}>
                {currentReferenceType === REFERENCE_TYPE.figure &&
                    <Flex pt={1} onClick={(e) => {
                      e.stopPropagation()
                      toggleSelection(ref)
                    }}>
                        <Checkbox
                            isChecked={selectedReferences.includes(ref)}
                            onChange={() => toggleSelection(ref)}>
                            <Tooltip hasArrow bg={'#FFF'} padding={0} label={
                              <Box>
                                {ref.content?.indexOf('data:image/png;base64') > - 1 ?
                                  <Image     alt="Reference Preview"
                                             maxWidth="200px"
                                             maxHeight="200px" src={ref.content || ref.img_url} />
                                  : <Image     alt="Reference Preview"
                                               maxWidth="200px"
                                               maxHeight="200px" src={ref.content || ref.img_url} />}
                              </Box>
                            } aria-label='Description'>
                              {ref.label || ref.caption || (`Figure${ref.id}`)}
                            </Tooltip>
                        </Checkbox>
                    </Flex>
                }
              </Box>
          ))}
          {crossRef && crossRef?.table.map((ref) => (
              <Box key={ref.id}>
                {currentReferenceType === REFERENCE_TYPE.table &&
                    <Flex pt={1} onClick={(e) => {
                      e.stopPropagation()
                      toggleSelection(ref)
                    }}>
                        <Checkbox
                            isChecked={selectedReferences.includes(ref)}
                            onChange={() => toggleSelection(ref)}>
                            <Tooltip hasArrow padding={4} bg={'#FFF'} color={'#333'} label={
                              <Box>
                                { ref.img_url ? <Image     alt="Reference Preview"
                                  maxWidth="200px"
                                  maxHeight="200px" src={ ref.img_url} /> :  
                                <TableRender content={ref.content || ref.caption || ref.latex  || ref.img_url} />}
                              </Box>
                             
                            } aria-label='Description'>
                              {ref.label || ref.caption}
                            </Tooltip>
                        </Checkbox>
                    </Flex>
                }
              </Box>
          ))}
        </Flex>
        <Flex flexDir={'row'} justifyContent={'space-between'} pt={1} pb={2}>
          <Box>
            <Button w={pxToRem(120)} h={pxToRem(30)}
                    size={"sm"}
                    variant={'outline'}
                    borderColor={'#d3d7e2'}
                    leftIcon={
                      <Image bg={'#C9D9E8'} w="1rem" p={'2px'} borderRadius={pxToRem(5)}
                             src="/static/editor/login.svg" alt="Refer button"/>
                    }
                    onClick={insertReference}
            >
              Add
            </Button>
          </Box>
        </Flex>
      </Flex>
  )
}

export default CrossReferencePanel;